import React, { useState, useEffect, useRef } from "react";
import { IconButton, Divider, Menu, MenuItem, Badge } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { useDispatch, useSelector } from "react-redux";
import icons from "../../assets/icons/icon-collection.svg";
import smallpholder from "../../assets/icons/small-placeholder.svg";
import placeholder from "../../assets/icons/placeholder.svg";
import Notifications from "./Notifications";
import { Link, useHistory, useLocation } from "react-router-dom";
import { logoutAction } from "../../redux/actions/authActions";
import { ternaryResolver } from "../../utils/helpers";
import {
  clearJobCreated,
  searchJobAction,
  setLocationFilterAction,
} from "../../redux/actions/jobActions";
import { getCountryAction } from "../../redux/actions/countryActions";
import { searchJobAction as businessSearchJobAction } from "../../redux/actions/businessActions";
import cogoToast from "cogo-toast";
import { getNotifications, getUnreadNotificationCount } from "../../redux/actions/settingsActions";
import AddNewPost from "../NewPost";
import MobileActionMenu from "./MobileActionMenu";

const REGIONS = ["EMEA", "AMER", "APAC", "LATAM", "GLOBAL"];

const ToolbarMenu = ({ onPostJob, onRequestHiringSupport, onUploadJobs }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [newPost, setNewPost] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileActionMenuOpen, setMobileActionMenuOpen] = useState(false);

  const { locationFilter } = useSelector(({ job }) => job);
  const { countries } = useSelector(({ country }) => country);
  const { userData } = useSelector((store) => store.auth);
  const { unreadNotificationCount } = useSelector((store) => store.settings);
  const { first_name, last_name, image_url } = userData;
  const resolvedName = ternaryResolver(first_name, `${first_name}`, "");

  const isHomePage = pathname === "/home" || pathname === "/";

  const lastUnreadCountRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize notification count on mount
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        await getNotifications(dispatch);
        const action = await getUnreadNotificationCount(dispatch);
        const count = action?.payload;
        if (typeof count === "number") {
          lastUnreadCountRef.current = count;
        }
      } catch (e) {
        // ignore
      }
    };

    initializeNotifications();
  }, [dispatch]);

  // Poll for new notifications every 20 seconds
  useEffect(() => {
    let mounted = true;

    const refreshNotifications = async () => {
      try {
        await getNotifications(dispatch);
        const action = await getUnreadNotificationCount(dispatch);
        const nextCount = action?.payload;
        if (mounted && typeof nextCount === "number" && lastUnreadCountRef.current !== null) {
          if (nextCount > lastUnreadCountRef.current) {
            cogoToast.info("New notification received", { position: "top-center", hideAfter: 3 });
          }
          lastUnreadCountRef.current = nextCount;
        }
      } catch (e) {
        // ignore
      }
    };

    const interval = setInterval(refreshNotifications, 20000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [dispatch]);

  useEffect(() => {
    if (countries.length === 0) {
      getCountryAction(dispatch);
    }
  }, []);

  // Build options list: regions first, then countries
  const locationOptions = [
    ...REGIONS.map((r) => ({ label: r, type: "region", value: r })),
    ...countries.map((c) => ({ label: c.name, type: "country", value: c.id })),
  ];

  const locationFilterValue = locationFilter
    ? locationOptions.find(
        (o) => o.type === locationFilter.type && o.value === locationFilter.value
      ) || null
    : null;

  const handleLocationFilterChange = (_e, option) => {
    if (!option) {
      setLocationFilterAction(dispatch, null);
    } else if (option.type === "region") {
      setLocationFilterAction(dispatch, { type: "region", value: option.value });
    } else {
      setLocationFilterAction(dispatch, { type: "country", value: option.value });
    }
    if (!isHomePage) history.push("/home");
  };

  const handleNewPost = () => {
    clearJobCreated(dispatch);
    setNewPost(true);
  };

  const handleMobilePlusClick = () => {
    setMobileActionMenuOpen(true);
  };

  const handleMobilePostJob = () => {
    if (onPostJob) {
      onPostJob();
    } else {
      handleNewPost();
    }
  };

  const handleMobileRequestHiring = () => {
    if (onRequestHiringSupport) {
      onRequestHiringSupport();
    }
  };

  const handleMobileUploadJobs = () => {
    if (onUploadJobs) {
      onUploadJobs();
    }
  };

  const handleNewPostClose = () => {
    setSuccess(false);
    setNewPost(false);
  };

  const isBusinessUser = () => {
    const accountType = localStorage.getItem("accountType");
    return accountType === "business";
  };

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      setIsSearching(true);

      const onSearchFinish = () => {
        setTimeout(() => {
          setIsSearching(false);
          history.push("/home");
        }, 600);
      };

      try {
        if (isBusinessUser()) {
          businessSearchJobAction(dispatch, searchTerm, onSearchFinish);
        } else {
          await searchJobAction(dispatch, searchTerm, onSearchFinish, locationFilter);
        }
      } catch (error) {
        console.error("Search failed:", error);
        setIsSearching(false);
      }
    } else {
      setShowSearchBar(!showSearchBar);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="w-100">
      <div className={`d-flex align-items-center justify-content-end ${isMobile ? "flex-wrap" : ""}`}>

        {/* Search Bar */}
        {showSearchBar && (
          <div
            className={`search-container d-flex align-items-center ${isMobile ? "w-100 order-1 mb-2" : ""}`}
            style={{ gap: 8, flex: 1, marginRight: 8 }}
          >
            <input
              className="toolbar-search-input"
              placeholder={isSearching ? "Searching..." : "Search"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSearching}
            />
            {isHomePage && (
              <Autocomplete
                options={locationOptions}
                getOptionLabel={(o) => o.label}
                groupBy={(o) => o.type === "region" ? "Regions" : "Countries"}
                loading={countries.length === 0}
                loadingText="Loading locations..."
                forcePopupIcon={false}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.toLowerCase();
                  return options.filter(
                    (o) => o.type === "region" || o.label.toLowerCase().includes(q)
                  );
                }}
                value={locationFilterValue}
                onChange={handleLocationFilterChange}
                style={{ flex: "1 1 150px", minWidth: 140 }}
                classes={{ root: "location-autocomplete" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Location"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, endAdornment: null }}
                    InputLabelProps={{ shrink: false }}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: 0 },
                    }}
                  />
                )}
              />
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className={`d-flex align-items-center ${isMobile && showSearchBar ? "order-2 w-100 justify-content-between" : ""}`}>
          <div className="d-flex align-items-center">
            <IconButton
              onClick={handleSearch}
              disabled={isSearching}
              className="search-button"
            >
              {isSearching ? (
                <div className="search-spinner-container">
                  <div className="spinner-ring"></div>
                </div>
              ) : (
                <svg width="18" height="16">
                  <use href={`${icons}#search`}></use>
                </svg>
              )}
            </IconButton>

            <IconButton onClick={(e) => setNotificationsAnchorEl(e.currentTarget)}>
              <Badge badgeContent={unreadNotificationCount} color="error">
                <svg width="16" height="18">
                  <use href={`${icons}#bell`}></use>
                </svg>
              </Badge>
            </IconButton>

            <IconButton className="d-md-none" onClick={handleMobilePlusClick}>
              <svg width="12" height="12">
                <use href={`${icons}#plus`}></use>
              </svg>
            </IconButton>
          </div>

          <AddNewPost
            open={newPost}
            handleClose={handleNewPostClose}
            setSuccess={setSuccess}
            success={success}
          />

          <MobileActionMenu
            open={mobileActionMenuOpen}
            onClose={() => setMobileActionMenuOpen(false)}
            onPostJob={handleMobilePostJob}
            onRequestHiring={handleMobileRequestHiring}
            onUploadJobs={handleMobileUploadJobs}
          />

          <Notifications
            anchorEl={notificationsAnchorEl}
            handleClose={() => setNotificationsAnchorEl(null)}
          />

          {!isMobile && <Divider orientation="vertical" flexItem />}

          {/* Profile Section */}
          <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <img
              src={image_url || placeholder}
              width={isMobile ? "32" : "40"}
              height={isMobile ? "32" : "40"}
              style={{
                borderRadius: "100px",
                marginLeft: isMobile ? "8px" : "10px",
                objectFit: "cover",
              }}
              alt="Profile"
              onError={({ target }) => { target.src = placeholder; }}
            />
            {!isMobile && <span className="mx-2">{resolvedName}</span>}
            <IconButton size="small">
              <svg width="8" height="6">
                <use href={`${icons}#arrowdown`}></use>
              </svg>
            </IconButton>
          </div>
        </div>

        {/* Profile Dropdown Menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            style: {
              width: isMobile ? "90vw" : "15rem",
              maxWidth: "15rem",
              marginTop: "0.5rem",
              borderRadius: "1rem",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            },
          }}
        >
          <MenuItem disabled>
            <img
              src={image_url || smallpholder}
              width="30"
              height="30"
              style={{ borderRadius: "100px" }}
              alt=""
            />
            <span className="mx-2" style={{ color: "#000", fontWeight: "bold" }}>{resolvedName}</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/profile">
            <span className="px-2 py-1 medium-text">Profile</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/help">
            <span className="px-2 py-1 medium-text">Help/Support</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/about">
            <span className="px-2 py-1 medium-text">About workbrook</span>
          </MenuItem>
          <MenuItem onClick={() => setAnchorEl(null)} component={Link} to="/settings/security">
            <span className="px-2 py-1 medium-text">Change Password</span>
          </MenuItem>
          <Divider />
          <MenuItem onClick={logoutAction} className="px-4 py-3 co-lightred medium-text">
            Logout of workbrook
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default ToolbarMenu;
